// src/pages/projects/components/TransactionsTable.jsx
import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  IconButton,
  Chip
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Receipt as InvoiceIcon } from '@mui/icons-material';
import { ExpenseIcon, IncomeIcon } from './TransactionIcons';
import { formatDate, formatCurrency } from '../../../utils/budgetUtils';
import { TRANSACTION_CATEGORIES } from '../../../constants/budgetConstants';

/**
 * Component to display the transactions table
 * @param {Object} props - Component props
 * @param {Array} props.transactions - Array of transaction objects
 * @param {boolean} props.canEdit - Whether the user can edit transactions
 * @param {Function} props.onAddTransaction - Function to handle adding a transaction
 * @param {Function} props.onDeleteTransaction - Function to handle deleting a transaction
 * @returns {JSX.Element} Transactions table component
 */
const TransactionsTable = ({ 
  transactions = [], 
  canEdit, 
  onAddTransaction,
  onDeleteTransaction
}) => {
  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Category</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Invoice</TableCell>
              {canEdit && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.length > 0 ? (
              transactions.map((transaction) => (
                <TableRow key={transaction._id}>
                  <TableCell>
                    <Chip 
                      icon={transaction.type === 'expense' ? <ExpenseIcon /> : <IncomeIcon />}
                      label={transaction.type === 'expense' ? 'Expense' : 'Income'}
                      color={transaction.type === 'expense' ? 'error' : 'success'}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(transaction.date)}</TableCell>
                  <TableCell>{transaction.description}</TableCell>
                  <TableCell>
                    {TRANSACTION_CATEGORIES[transaction.type]?.find(cat => cat.value === transaction.category)?.label || 
                      transaction.category}
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      color={transaction.type === 'expense' ? 'error.main' : 'success.main'}
                      fontWeight="medium"
                    >
                      {transaction.type === 'expense' ? '-' : '+'}{formatCurrency(transaction.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {transaction.isInvoiced && (
                      <Chip 
                        icon={<InvoiceIcon />}
                        label={transaction.invoiceNumber || 'Invoiced'}
                        size="small"
                        color="info"
                      />
                    )}
                  </TableCell>
                  {canEdit && (
                    <TableCell align="right">
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => onDeleteTransaction(transaction)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={canEdit ? 7 : 6} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="textSecondary">
                    No transactions recorded yet.
                  </Typography>
                  {canEdit && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={onAddTransaction}
                      sx={{ mt: 1 }}
                    >
                      Add Transaction
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default TransactionsTable;